import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { Reveal } from "../lib/reveal";
import { useSeo, breadcrumbJsonLd } from "../lib/seo";
import { services } from "../lib/content";
import { CTABlock } from "./ServicesPage";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  useSeo({
    title: service ? `${service.title} — Services` : "Service",
    description: service ? service.summary : "",
    path: `/services/${slug}`,
    jsonLd: service && [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.title,
        description: service.summary,
        provider: { "@type": "Organization", name: "Wald Enterprise" },
        areaServed: "ZA",
      },
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: service.title, path: `/services/${slug}` },
      ]),
    ],
  });
  if (!service) return <Navigate to="/services" replace />;
  const idx = services.findIndex((s) => s.slug === slug);
  const next = services[(idx + 1) % services.length];
  return (
    <SiteLayout>
      <section className="border-b border-border">
        <div className="container-wald py-12">
          <Link to="/services" data-testid="service-back" className="inline-flex items-center gap-2 text-sm opacity-70 hover:opacity-100">
            <ArrowLeft size={14} strokeWidth={2} /> All services
          </Link>
        </div>
      </section>
      <section className="border-b border-border">
        <div className="container-wald py-16 md:py-24">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-8">
              <div className="font-mono-tab text-xs opacity-60 mb-6">SERVICE / {service.number}</div>
              <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight">{service.title}</h1>
              <p className="mt-6 text-lg md:text-xl opacity-90 max-w-2xl leading-snug">{service.tagline}</p>
              <p className="mt-4 opacity-70 max-w-2xl leading-relaxed">{service.summary}</p>
            </div>
          </div>
        </div>
      </section>
      <section className="border-b border-border">
        <div className="container-wald py-20">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="eyebrow mb-4">Outcomes we ship</div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight max-w-md">
                What our clients actually take away.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7">
              <ul className="border-t border-border">
                {service.outcomes.map((o) => (
                  <Reveal key={o} className="border-b border-border py-5 flex gap-4">
                    <Check size={18} className="text-[color:var(--wald-copper)] mt-0.5 shrink-0" strokeWidth={2} />
                    <span className="text-base leading-relaxed">{o}</span>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      <section className="border-b border-border bg-card/30">
        <div className="container-wald py-20">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="eyebrow mb-4">Our approach</div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight max-w-md">
                How we work when we start.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-6">
              {service.approach.map((a, i) => (
                <Reveal key={a} delay={i * 40} className="flex gap-6">
                  <span className="font-mono-tab text-xs opacity-50 pt-1">0{i + 1}</span>
                  <p className="text-base opacity-90 leading-relaxed">{a}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section className="border-b border-border">
        <div className="container-wald py-12 flex justify-between items-center flex-wrap gap-4">
          <div className="opacity-60 text-sm">Continue</div>
          <Link to={`/services/${next.slug}`} data-testid="service-next" className="inline-flex items-center gap-3 group">
            <div className="text-right">
              <div className="eyebrow">Next service</div>
              <div className="font-display text-xl tracking-tight">{next.title}</div>
            </div>
            <ArrowRight size={18} strokeWidth={2} className="group-hover:text-[color:var(--wald-copper)]" />
          </Link>
        </div>
      </section>
      <CTABlock />
    </SiteLayout>
  );
}
